import * as React from 'react';
import {
  ArrowRightLeft,
  ChartBar,
  ChartCandlestick,
  ChevronsUpDown,
  History,
  LogOut,
  Settings,
  Wallet,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth, useLanguage, useNetwork } from '@/src/contexts';
import {
  NETWORK_MAP,
  TranslationKey,
  getText,
} from '@/src/helpers';
import { useFireblockVaultAccountAssetAddressAtIndex } from '@/src/hooks';
import { Avatar, AvatarFallback } from '@/src/ui-components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/src/ui-components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from '@/src/ui-components/ui/sidebar';

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const location = useLocation();
  const pathname = location.pathname;
  const { network } = useNetwork();
  const { language } = useLanguage();
  const { user, signOut } = useAuth();

  // Get XRPL address from Fireblocks
  const { vaultAccountAssetAddress } = useFireblockVaultAccountAssetAddressAtIndex();
  const xrplAddress = vaultAccountAssetAddress?.address;

  const getTranslation = (key: TranslationKey) => getText(language, key);

  // Navigation items mapped from your current navbar
  const navigation = [
    {
      id: 'trading',
      name: getTranslation('trading' as TranslationKey),
      href: '/trading',
      icon: ChartCandlestick,
    },
    {
      id: 'swap',
      name: getTranslation('swap' as TranslationKey),
      href: '/swap',
      icon: ArrowRightLeft,
    },
    {
      id: 'wallet',
      name: getTranslation('wallet' as TranslationKey),
      href: '/wallet',
      icon: Wallet,
    },
    {
      id: 'history',
      name: getTranslation('history' as TranslationKey),
      href: '/history',
      icon: History,
    },
    {
      id: 'oracles',
      name: getTranslation('oracles' as TranslationKey),
      href: '/oracles',
      icon: ChartBar,
    },
  ];

  const currentNetwork = NETWORK_MAP[network];

  return (
    <Sidebar collapsible="icon" {...props} className="pb-4">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" className="cursor-default hover:bg-transparent">
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                <img
                  src="/logo.svg"
                  alt="Acacia"
                  className="size-6"
                />
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">Acacia DEX</span>
                <span className="truncate text-xs">{currentNetwork.name}</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarMenu>
            {navigation.map((item) => (
              <SidebarMenuItem key={item.id}>
                <SidebarMenuButton asChild isActive={pathname === item.href}>
                  <Link to={item.href}>
                    <item.icon />
                    <span>{item.name}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarFallback className="rounded-lg">
                      {user?.email?.substring(0, 2).toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      {user?.email || 'User'}
                    </span>
                    <span className="truncate text-xs">Account</span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side="bottom"
                align="end"
                sideOffset={4}
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarFallback className="rounded-lg">
                        {user?.email?.substring(0, 2).toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">
                        {user?.email || 'User'}
                      </span>
                      <span className="truncate text-xs font-mono">
                        {xrplAddress || 'Loading address...'}
                      </span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/preferences">
                    <Settings />
                    Preferences
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut}>
                  <LogOut />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
